/**
 * Pricing Calculator Utilities
 * Handles custom delivery count pricing based on existing tiers
 */

/**
 * Calculate price for custom delivery count using pricing tiers
 * Uses linear interpolation between tiers or pro-rata from nearest tier
 * 
 * @param {number} customCount - The custom delivery count
 * @param {Array} pricingTiers - Array of {quantity, price} objects
 * @returns {number} - Calculated price
 */
export function calculateCustomPrice(customCount, pricingTiers) {
  if (!pricingTiers || pricingTiers.length === 0) {
    return 0;
  }
  
  // Sort tiers by quantity
  const sortedTiers = [...pricingTiers]
    .filter(t => t.quantity && t.price > 0)
    .sort((a, b) => a.quantity - b.quantity);
  
  if (sortedTiers.length === 0) {
    return 0;
  }
  
  // Check if exact tier exists
  const exactTier = sortedTiers.find(t => t.quantity === customCount);
  if (exactTier) {
    return exactTier.price;
  }
  
  // Find tiers above and below custom count
  let lowerTier = null;
  let upperTier = null;
  
  for (let i = 0; i < sortedTiers.length; i++) {
    if (sortedTiers[i].quantity < customCount) {
      lowerTier = sortedTiers[i];
    } else if (sortedTiers[i].quantity > customCount) {
      upperTier = sortedTiers[i];
      break;
    }
  }
  
  // Case 1: Custom count is between two tiers - use linear interpolation
  if (lowerTier && upperTier) {
    const qtyDiff = upperTier.quantity - lowerTier.quantity;
    const priceDiff = upperTier.price - lowerTier.price;
    const ratio = (customCount - lowerTier.quantity) / qtyDiff;
    const interpolatedPrice = lowerTier.price + (priceDiff * ratio);
    
    console.log(`💰 Interpolated: ${customCount} meals between ${lowerTier.quantity} ($${lowerTier.price}) and ${upperTier.quantity} ($${upperTier.price}) = $${interpolatedPrice.toFixed(2)}`);
    return parseFloat(interpolatedPrice.toFixed(2));
  }
  
  // Case 2: Custom count is below lowest tier - use pro-rata from lowest tier
  if (!lowerTier && upperTier) {
    const pricePerDelivery = upperTier.price / upperTier.quantity;
    const calculatedPrice = pricePerDelivery * customCount;
    
    console.log(`💰 Below lowest tier: ${customCount} meals × $${pricePerDelivery.toFixed(2)}/meal = $${calculatedPrice.toFixed(2)}`);
    return parseFloat(calculatedPrice.toFixed(2));
  }
  
  // Case 3: Custom count is above highest tier - use pro-rata from highest tier
  if (lowerTier && !upperTier) {
    const pricePerDelivery = lowerTier.price / lowerTier.quantity;
    const calculatedPrice = pricePerDelivery * customCount;
    
    console.log(`💰 Above highest tier: ${customCount} meals × $${pricePerDelivery.toFixed(2)}/meal = $${calculatedPrice.toFixed(2)}`);
    return parseFloat(calculatedPrice.toFixed(2));
  }
  
  // Fallback: Use the only tier available
  const onlyTier = sortedTiers[0];
  const pricePerDelivery = onlyTier.price / onlyTier.quantity;
  const calculatedPrice = pricePerDelivery * customCount;
  
  console.log(`💰 Single tier fallback: ${customCount} meals × $${pricePerDelivery.toFixed(2)}/meal = $${calculatedPrice.toFixed(2)}`);
  return parseFloat(calculatedPrice.toFixed(2));
}

/**
 * Check if a meal plan can be priced for a custom count
 * Returns true if we can calculate a price based on existing tiers
 */
export function canPriceForCustomCount(pricingTiers) {
  return pricingTiers && pricingTiers.length > 0 && 
         pricingTiers.some(t => t.quantity && t.price > 0);
}
